import { apiDelete, apiPost, responseErrorMessage } from './api_v2';
import db, { clearFingerprint } from './db';
import { clearWidgetGenerationActivityDismissalsForChat } from './widgetGenerationNotifications';
import { clearPendingWidgetGenerationTurn } from './widgetGenerationTurns';

export const WIDGET_REMOVAL_ACTION = Object.freeze({
  DELETE: 'delete',
  UNPUBLISH: 'unpublish',
});

export function isUserCreatedWidget(widget) {
  if (!widget) return false;
  if (typeof widget.user_created === 'boolean') return widget.user_created;

  // Rolling-deploy fallback for cached rows written before `user_created` was
  // serialized. Generated template slugs are stable even if chat linkage changes.
  return widget.template_runtime_state == null || String(widget.template_slug || '').startsWith('generated_');
}

export function isInstalledTemplateWidget(widget) {
  return Boolean(widget && !isUserCreatedWidget(widget));
}

export function widgetRemovalAction(widget) {
  if (!widget) return null;
  if (isInstalledTemplateWidget(widget)) return WIDGET_REMOVAL_ACTION.DELETE;

  return widget.status === 'published' ? WIDGET_REMOVAL_ACTION.UNPUBLISH : WIDGET_REMOVAL_ACTION.DELETE;
}

export function canRemoveWidgetFromBoard(widget, { currentProfileId, isAdmin } = {}) {
  if (!widget) return false;
  if (isInstalledTemplateWidget(widget)) return true;

  return Boolean(isAdmin || (currentProfileId && widget.profile_id === currentProfileId));
}

export async function removeWidget(widget, action = widgetRemovalAction(widget)) {
  if (!widget || !action) throw new Error('widget removal requires a widget');

  const response =
    action === WIDGET_REMOVAL_ACTION.UNPUBLISH
      ? await apiPost(`/widgets/${encodeURIComponent(widget.id)}/unpublish`)
      : await apiDelete(`/widgets/${encodeURIComponent(widget.id)}`);
  const data = await response.json();

  if (!response.ok || !data.ok) {
    throw new Error(responseErrorMessage(data, `Could not ${action} widget.`));
  }

  if (action === WIDGET_REMOVAL_ACTION.UNPUBLISH) {
    if (data.data?.widget) await db.widgets.put(data.data.widget);
  } else {
    await db.transaction('rw', db.widgets, db.widget_runtime_titles, db.agent_chats, async () => {
      await db.widgets.delete([widget.workspace_id, widget.id]);
      await db.widget_runtime_titles.delete([widget.workspace_id, widget.id]);
      if (widget.agent_chat_id) await db.agent_chats.delete([widget.workspace_id, widget.agent_chat_id]);
    });
    if (widget.agent_chat_id) {
      clearPendingWidgetGenerationTurn(widget.workspace_id, widget.agent_chat_id);
      clearWidgetGenerationActivityDismissalsForChat(widget.workspace_id, widget.agent_chat_id);
    }
  }

  const fingerprintKeys = ['widgets', 'board_items'];
  if (action === WIDGET_REMOVAL_ACTION.DELETE && widget.agent_chat_id) fingerprintKeys.push('agent_chats');
  await Promise.all(fingerprintKeys.map((key) => clearFingerprint(key)));
  return { action, widget: data.data?.widget || null };
}
